import {
  LifecycleStages,
  LocaleType,
  mergeLocales,
  Univer
} from '@univerjs/core';
import DesignEnUS from '@univerjs/design/locale/en-US';
import { UniverDocsPlugin } from '@univerjs/docs';
import { UniverDocsUIPlugin } from '@univerjs/docs-ui';
import DocsUIEnUS from '@univerjs/docs-ui/locale/en-US';
import { UniverFormulaEnginePlugin } from '@univerjs/engine-formula';
import { UniverRenderEnginePlugin } from '@univerjs/engine-render';
import { UniverSheetsPlugin } from '@univerjs/sheets';
import { UniverSheetsFormulaUIPlugin } from '@univerjs/sheets-formula-ui';
import SheetsFormulaUIEnUS from '@univerjs/sheets-formula-ui/locale/en-US';
import { UniverSheetsNumfmtUIPlugin } from '@univerjs/sheets-numfmt-ui';
import SheetsNumfmtUIEnUS from '@univerjs/sheets-numfmt-ui/locale/en-US';
import { UniverSheetsUIPlugin } from '@univerjs/sheets-ui';
import SheetsUIEnUS from '@univerjs/sheets-ui/locale/en-US';
import SheetsEnUS from '@univerjs/sheets/locale/en-US';
import { UniverUIPlugin } from '@univerjs/ui';
import UIEnUS from '@univerjs/ui/locale/en-US';
import { UniverSheetsFilterPlugin } from '@univerjs/sheets-filter';
import { UniverSheetsFilterUIPlugin } from '@univerjs/sheets-filter-ui';
import SheetsFilterUIEnUS from '@univerjs/sheets-filter-ui/locale/en-US';
import { UniverSheetsSortPlugin } from '@univerjs/sheets-sort';
import { UniverSheetsSortUIPlugin } from '@univerjs/sheets-sort-ui';
import SheetsSortUIEnUS from '@univerjs/sheets-sort-ui/locale/en-US';
import { FUniver } from '@univerjs/core/facade';
import type {
  CreateUniverInstanceOptions,
  RegisterCellRenderOnLifecycleOptions
} from './spreadsheetTypes';
import { UniverFindReplacePlugin } from '@univerjs/find-replace';
import { UniverSheetsFindReplacePlugin } from '@univerjs/sheets-find-replace';
import FindReplaceUIEnUS from '@univerjs/find-replace/locale/en-US';
import SheetsFindReplaceUIEnUS from '@univerjs/sheets-find-replace/locale/en-US';

export type {
  CreateUniverInstanceOptions,
  RegisterCellRenderOnLifecycleOptions
};

export function createUniverInstance(options: CreateUniverInstanceOptions): {
  univer: Univer;
  api: ReturnType<typeof FUniver.newAPI>;
} {
  const { container, menuHidden } = options;
  const univer = new Univer({
    locale: LocaleType.EN_US,
    locales: {
      [LocaleType.EN_US]: mergeLocales(
        DesignEnUS,
        UIEnUS,
        DocsUIEnUS,
        SheetsEnUS,
        SheetsUIEnUS,
        SheetsFormulaUIEnUS,
        SheetsNumfmtUIEnUS,
        SheetsFilterUIEnUS,
        SheetsSortUIEnUS,
        SheetsFindReplaceUIEnUS,
        FindReplaceUIEnUS,
        {
          permission: {
            dialog: {
              alertContent:
                'The spreadsheet is locked now. Please wait for the process to complete.',
              commonErr:
                'The spreadsheet is locked now. Please wait for the process to complete.',
              editErr:
                'The spreadsheet is locked now. Please wait for the process to complete.'
            }
          }
        }
      )
    }
  });

  univer.registerPlugin(UniverRenderEnginePlugin);
  univer.registerPlugin(UniverFormulaEnginePlugin);
  univer.registerPlugin(UniverUIPlugin, { container, ribbonType: 'simple' });
  univer.registerPlugin(UniverDocsPlugin);
  univer.registerPlugin(UniverDocsUIPlugin);
  univer.registerPlugin(UniverSheetsPlugin);
  univer.registerPlugin(UniverSheetsUIPlugin, {
    footer: { sheetBar: false },
    menu: menuHidden
  });
  univer.registerPlugin(UniverSheetsFilterPlugin);
  univer.registerPlugin(UniverSheetsFilterUIPlugin);
  univer.registerPlugin(UniverSheetsFormulaUIPlugin);
  univer.registerPlugin(UniverSheetsNumfmtUIPlugin);
  univer.registerPlugin(UniverSheetsSortPlugin);
  univer.registerPlugin(UniverSheetsSortUIPlugin);
  univer.registerPlugin(UniverFindReplacePlugin);
  univer.registerPlugin(UniverSheetsFindReplacePlugin);

  const api = FUniver.newAPI(univer);
  return { univer, api };
}

/**
 * Register a LifeCycleChanged listener that, on Rendered/Steady, registers cell custom renders
 * and sets the favicon-loaded repaint callback. Call from Spreadsheet after creating the Univer instance.
 */
export function registerCellRenderOnLifecycle(
  options: RegisterCellRenderOnLifecycleOptions
): void {
  const {
    univerAPI,
    cellRenders,
    setFaviconLoadedCallback,
    requestRepaint,
    runSetup,
    cellRenderDisposableRef
  } = options;
  const lifecycleDisposable = univerAPI.addEvent(
    univerAPI.Event.LifeCycleChanged,
    (params: { stage: LifecycleStages }) => {
      if (
        params.stage === LifecycleStages.Rendered ||
        params.stage === LifecycleStages.Steady
      ) {
        lifecycleDisposable?.dispose?.();
        requestAnimationFrame(() => {
          cellRenderDisposableRef.current?.dispose?.();
          cellRenderDisposableRef.current = univerAPI
            .getSheetHooks()
            .onCellRender(cellRenders);
          setFaviconLoadedCallback(() => {
            requestAnimationFrame(requestRepaint);
          });
        });
        requestAnimationFrame(runSetup);
      }
    }
  );
  if (univerAPI.getCurrentLifecycleStage() >= LifecycleStages.Rendered) {
    requestAnimationFrame(runSetup);
  }
}
